/**
 * Where a REACT_APP_* value comes from. First match wins:
 *
 *   1. runtime  — `window.__ORBIEE_ENV__`, written by docker/env-config.sh
 *                 from the container's environment when it starts: the
 *                 values the deploy pipeline sets with
 *                 `gcloud run deploy --set-env-vars` / `--set-secrets`.
 *                 public/env-config.js ships an empty object, so a local run
 *                 or a plain build behaves as if nothing was set.
 *   2. build    — `process.env.REACT_APP_*`, inlined by CRA at `npm run build`
 *                 (.env on a laptop, `--build-arg` in Docker).
 *   3. default  — the dev value in the calling config module.
 *
 * Runtime beats build on purpose: a value set at deploy time is the most
 * specific statement about the environment the container is running in, and
 * it is the one the pipeline owns (2026-10-12).
 *
 * Callers pass the build-time value themselves, as a literal
 * `process.env.REACT_APP_X` member access: that is the form CRA's
 * compile-time replacement recognises. A computed `process.env[name]` here
 * would not be inlined, so this helper never touches `process.env`.
 * (Wording note: Tailwind scans every word under src/ for class names, so a
 * CSS keyword used as a plain word in a comment here adds a rule to the
 * bundle — the first draft of this paragraph did exactly that.)
 */

export const RUNTIME_ENV_GLOBAL = "__ORBIEE_ENV__";

/**
 * @param {string} name            e.g. "REACT_APP_API_BASE_URL"
 * @param {string|undefined} buildTimeValue  the literal `process.env.<name>`
 * @returns {string|undefined} the runtime value if one is set and non-empty,
 *   else the build-time value (which may be undefined — the caller applies
 *   its default with `||`).
 */
export function envValue(name, buildTimeValue) {
  const runtime =
    typeof window !== "undefined" && window[RUNTIME_ENV_GLOBAL]
      ? window[RUNTIME_ENV_GLOBAL][name]
      : undefined;
  // Trimmed because these arrive from a shell: a stray trailing space in a
  // --set-env-vars value would otherwise end up inside every URL.
  if (typeof runtime === "string" && runtime.trim() !== "") return runtime.trim();
  return buildTimeValue;
}
